import { useTranslations } from 'next-intl';
import { EntityDrawerSections } from '../../../components/entity-drawer';
import { EntityDrawerSectionMarkdown } from '../../../components/entity-drawer/EntityDrawerSectionMarkdown';
import type { InternalEnrichedFinding } from '../../../types';
import { Box, VStack } from '@terra/ui-styled-system';
import { DrawerCollapsible } from '../../custom-drawer';
import { TradeUpIcon } from '@terra/ui-icons';
import { Text } from '../../../components/text';
import { MarkdownPreview } from '../..';
import { Skeleton } from '../../../components/skeleton';

type FindingDrawerBusinessImpactTabProps = {
  finding: InternalEnrichedFinding;
  useBoxCollapsibles?: boolean;
  isDetailLoading?: boolean;
};

export const FindingDrawerBusinessImpactTab = ({
  finding,
  useBoxCollapsibles = false,
  isDetailLoading = false,
}: FindingDrawerBusinessImpactTabProps) => {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.businessImpact',
  );
  const noDataTranslation = t('noData');

  if (isDetailLoading) {
    return (
      <EntityDrawerSections gap={useBoxCollapsibles ? '6' : undefined}>
        <VStack gap={3} w="full" alignItems="stretch" py={2}>
          <Skeleton h="96px" w="full" borderRadius="lg" />
          <Skeleton h="72px" w="full" borderRadius="lg" />
        </VStack>
      </EntityDrawerSections>
    );
  }

  if (!finding.businessImpact) {
    return (
      <EntityDrawerSections gap={useBoxCollapsibles ? '6' : undefined}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="full"
          h="full"
          py={12}
        >
          <VStack gap={4} alignItems="center" maxW="md">
            <TradeUpIcon size={24} color="var(--colors-fg-muted)" />
            <VStack gap={2} alignItems="center">
              <Text fontSize="sm" fontWeight="medium" color="fg.default">
                {t('noDataTitle')}
              </Text>
              <Text fontSize="xs" color="fg.muted" textAlign="center">
                {noDataTranslation}
              </Text>
            </VStack>
          </VStack>
        </Box>
      </EntityDrawerSections>
    );
  }

  if (useBoxCollapsibles) {
    return (
      <EntityDrawerSections gap="6">
        <DrawerCollapsible
          variant="box"
          title={t('businessImpactDetails')}
          defaultOpen={true}
        >
          <MarkdownPreview source={finding.businessImpact} fontSize={14} />
        </DrawerCollapsible>
      </EntityDrawerSections>
    );
  }

  return (
    <EntityDrawerSections>
      <EntityDrawerSectionMarkdown
        title={t('businessImpactDetails')}
        value={finding.businessImpact}
      />
    </EntityDrawerSections>
  );
};
